import isEqual from 'lodash/isEqual';
import { createDesignDefinition, validateDesignDefinition } from '../design-tokens';
import type { BrandContext, BrandGuidelines, BrandResources } from './types';
import type { DesignDefinition } from '../design-tokens';
import { isValidUrl } from '../utils/url/is-valid-url';

export function createBrandResources(): BrandResources {
	return { design: createDesignDefinition(), links: [], guidelines: { entries: [] } };
}

type StoryBrandSettings = { design?: DesignDefinition; brandContext?: BrandContext };

// Editor defaults are transient: reading an older Story never initializes persisted settings.
export function getStoryBrandResources(settings?: StoryBrandSettings): BrandResources {
	return {
		design: settings?.design ?? createDesignDefinition(),
		links: settings?.brandContext?.links ?? [],
		guidelines: settings?.brandContext?.guidelines ?? { entries: [] },
	};
}

export function updateStoryBrandResources<T extends StoryBrandSettings>(settings: T, next: BrandResources): T {
	const before = getStoryBrandResources(settings);
	return {
		...settings,
		...(!isEqual(before.design, next.design) ? { design: next.design } : {}),
		...(!isEqual(
			{ links: before.links, guidelines: before.guidelines },
			{ links: next.links, guidelines: next.guidelines }
		)
			? { brandContext: { links: next.links, guidelines: next.guidelines } }
			: {}),
	};
}

export function validateBrandResources(resources: BrandResources): BrandResources {
	const design = validateDesignDefinition(resources.design);
	return { ...validateBrandContext(resources), design };
}

export function validateBrandContext(resources: BrandContext): BrandContext {
	const ids = new Set<string>();
	for (const link of resources.links) {
		if (!link.id?.trim() || ids.has(link.id)) throw new Error('Brand link IDs must be unique and non-empty.');
		ids.add(link.id);
		if (!link.label.trim() || !/^https?:\/\/\S+$/i.test(link.url) || !isValidUrl(link.url))
			throw new Error('Every Brand link needs a label and an HTTP(S) URL.');
	}
	if (resources.links.filter(link => link.isPrimary).length > 1)
		throw new Error('Only one Brand link can be primary.');
	ids.clear();
	const guidelines = typeof resources.guidelines === 'string' ? { entries: [] } : resources.guidelines;
	for (const entry of guidelines.entries) {
		if (
			!entry.id?.trim() ||
			ids.has(entry.id) ||
			!entry.content.trim() ||
			(entry.title !== undefined && !entry.title.trim())
		)
			throw new Error(
				'Every guideline needs a unique ID, non-blank content and a non-blank heading when provided.'
			);
		ids.add(entry.id);
	}
	return structuredClone({ links: resources.links, guidelines });
}

export function copyBrandResources(resources: BrandResources): BrandResources {
	return validateBrandResources(resources);
}

export function areBrandResourcesEqual(before: BrandResources, after: BrandResources): boolean {
	try {
		return isEqual(validateBrandResources(before), validateBrandResources(after));
	} catch {
		return false;
	}
}

export function brandResourcesError(resources: BrandResources | undefined): string | undefined {
	if (!resources) return undefined;
	try {
		validateBrandResources(resources);
	} catch (error) {
		return error instanceof Error ? error.message : String(error);
	}
	return undefined;
}

export function getGeneralGuidelines(guidelines: BrandGuidelines): string {
	return guidelines.entries.find(entry => entry.id === 'general')?.content ?? '';
}

export function setGeneralGuidelines(guidelines: BrandGuidelines, content: string): BrandGuidelines {
	const general = guidelines.entries.find(entry => entry.id === 'general');
	const entries = content.trim()
		? general
			? guidelines.entries.map(entry => (entry.id === 'general' ? { ...entry, content } : entry))
			: [{ id: 'general', content }, ...guidelines.entries]
		: guidelines.entries.filter(entry => entry.id !== 'general');
	// The deliberately single-field UI never flattens or discards other imported/future entries.
	return { ...guidelines, entries };
}
